import React from "react"

import { ReactComponent as IconClock } from "@common_assets/svg/clock.svg"
import { ReactComponent as Trash } from "@common_assets/svg/trash.svg"
import { Checkbox, Fab, Stack, Tooltip, Typography } from "@mui/material"
import { ITeacherTransactions } from "api/reactQuery/queries/transaction.types"
import Coin from "components/common/icon/coin"
import IconWrapper from "components/common/icon/iconWrapper"
import { Cell, Row } from "components/common/table/basicTable"
import TransactionType from "components/common/transactionType"
import { getSignByTransactionType } from "components/module/studentProfile/transactions/transactions.utils"
import dayjs from "dayjs"
import { useTranslation } from "react-i18next"

import { isStudentStorePurchase } from "../AllTransactionsTeacher.utility"

interface BodyAllTransactionsProps {
  data: ITeacherTransactions
  onClick: () => void
  getChecked: boolean
  onChange: () => void
}

const BodyAllTransactions = ({
  data,
  onClick,
  getChecked,
  onChange,
}: BodyAllTransactionsProps) => {
  const { t } = useTranslation()
  const isPendingTransaction = data?.is_booked
  const isDeletable = !isStudentStorePurchase(data)
  const notDeletableReason = t(
    "allTransactions.studentStorePurchaseCantBeDeleted"
  )
  const selectLabel = t("allTransactions.selectTransaction", {
    title: data.title,
    student: `${data.student.first_name} ${data.student.last_name}`,
  })

  const getPersonOfTransaction = (data: ITeacherTransactions) => {
    if (data.teacher === null && data.helper === null) {
      return <span>-</span>
    }

    if (data.teacher) {
      return data.teacher?.last_name
    }

    if (data.helper) {
      return `Banker - ${data.helper?.last_name}`
    }
  }

  return (
    <Row>
      <Cell tableCellProps={{ width: "48px", sx: { p: 0 } }} px={0}>
        <Tooltip
          title={isDeletable ? "" : notDeletableReason}
          arrow={true}
          describeChild
        >
          <span tabIndex={isDeletable ? undefined : 0}>
            <Checkbox
              checked={getChecked}
              onChange={onChange}
              disabled={!isDeletable}
              slotProps={{
                input: {
                  "aria-label": isDeletable
                    ? selectLabel
                    : `${selectLabel}. ${notDeletableReason}`,
                },
              }}
            />
          </span>
        </Tooltip>
      </Cell>
      <Cell tableCellProps={{ width: "80px" }}>
        <Stack position="relative">
          <TransactionType transaction={data} />
          {!isPendingTransaction && (
            <IconWrapper
              height="19px"
              width="19px"
              sx={{
                position: "absolute",
                top: "1px",
                right: "-5px",
                backgroundColor: "white",
                borderRadius: "50%",
              }}
            >
              <IconClock />
            </IconWrapper>
          )}
        </Stack>
      </Cell>
      <Cell>
        <Stack>
          <Typography variant="body2" color="mockup.neutral10">
            {data.class_name}
          </Typography>
          <Typography
            variant="body2"
            color="mockup.neutral10"
            textAlign="center"
            fontWeight="500"
          >
            {getPersonOfTransaction(data)}
          </Typography>
        </Stack>
      </Cell>
      <Cell tableCellProps={{ width: "17%" }} sx={{ overflow: "hidden" }}>
        <Typography
          component="span"
          fontWeight="500"
          variant="body2"
          color="mockup.neutral10"
          noWrap
        >
          {data.student.first_name} {data.student.last_name}
        </Typography>
      </Cell>
      <Cell tableCellProps={{ width: "22%" }} sx={{ overflow: "hidden" }}>
        <Typography
          component="span"
          fontWeight="500"
          variant="body2"
          color="mockup.neutral10"
          noWrap
        >
          {data.title}
        </Typography>
      </Cell>
      <Cell>
        <Typography variant="body2" color="mockup.neutral10" fontWeight="500">
          {dayjs(data.created_at).format("MMM DD, YYYY")}
        </Typography>
      </Cell>
      <Cell tableCellProps={{ width: "10%" }}>
        <Coin
          amount={Math.abs(data.amount)}
          sign={
            data.amount !== 0
              ? data.amount > 0
                ? "+"
                : "-"
              : getSignByTransactionType(data.type)
          }
        />
      </Cell>
      <Cell tableCellProps={{ width: "48px", sx: { p: 0 } }} pl="8px" pr={0}>
        <Tooltip
          title={isDeletable ? "" : notDeletableReason}
          arrow={true}
          describeChild
        >
          <span>
            <Fab
              size="small"
              aria-label={t("allTransactions.deleteTransaction")}
              disabled={getChecked || !isDeletable}
              onClick={onClick}
            >
              <Trash />
            </Fab>
          </span>
        </Tooltip>
      </Cell>
    </Row>
  )
}
export default BodyAllTransactions
